// src/pages/hr/learning/LearningDetailsPanel.jsx
// ============================================================
// Learning & Development - Details Slide-out Panel
// Redesigned with Green Theme & Outline Icons
// ============================================================

import React from "react";
import {
  X,
  BookOpen,
  User,
  Clock,
  Building,
} from "lucide-react";

export default function LearningDetailsPanel({
  item, // Course or Employee object
  onClose,
}) {
  if (!item) return null;

  const isCourse = !!item.title;

  // Extract Employee Info
  const employee = item.employee || item.employeeObj || {
    displayName: item.displayName || item.employeeName || item.assignedToName || "Employee",
    id: item.employeeId || item.id,
    employeeId: item.empCode || item.employeeId || "EMP-100",
    department: item.department || "Engineering",
    designation: item.designation || item.role || "Team Member",
    reportingManager: item.reportingManager || "HR Manager",
    photoURL: item.photoURL || "",
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: "420px",
        maxWidth: "90vw",
        backgroundColor: "#ffffff",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid #e2e8f0",
      }}
    >
      {/* Drawer Header */}
      <div
        style={{
          padding: "1.35rem 1.5rem",
          background: "#1b4b31",
          color: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              border: "1.5px solid rgba(255,255,255,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
            }}
          >
            {isCourse ? <BookOpen size={20} strokeWidth={1.8} /> : <User size={20} strokeWidth={1.8} />}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800 }}>
              {isCourse ? item.title : employee.displayName}
            </h3>
            <p style={{ margin: "2px 0 0", fontSize: "0.76rem", opacity: 0.85 }}>
              {isCourse ? `${item.category || "General"} • ${item.duration || "30 Mins"}` : employee.designation}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: "50%",
            width: "30px",
            height: "30px",
            cursor: "pointer",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Drawer Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {isCourse ? (
          <>
            <div>
              <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                Course Overview
              </label>
              <p style={{ margin: "0.35rem 0 0", fontSize: "0.84rem", color: "#334155", lineHeight: 1.5 }}>
                {item.description || "Comprehensive corporate training curriculum and evaluation modules."}
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div style={{ background: "#f8fafc", padding: "0.85rem", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                  <Clock size={12} strokeWidth={1.8} /> Duration
                </span>
                <div style={{ fontSize: "0.92rem", fontWeight: 800, color: "#0f172a", marginTop: "4px" }}>
                  {item.duration || "45 Mins"}
                </div>
              </div>
              <div style={{ background: "#f8fafc", padding: "0.85rem", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                  <Building size={12} strokeWidth={1.8} /> Target Dept
                </span>
                <div style={{ fontSize: "0.92rem", fontWeight: 800, color: "#0f172a", marginTop: "4px" }}>
                  {item.department || "All Departments"}
                </div>
              </div>
            </div>

            {item.assignedToNames && item.assignedToNames.length > 0 && (
              <div>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                  Assigned Employees ({item.assignedToNames.length})
                </label>
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                  {item.assignedToNames.map((name, i) => (
                    <span key={i} style={{ background: "#f0fdf4", color: "#15803d", border: "1.5px solid #bbf7d0", padding: "0.2rem 0.6rem", borderRadius: "99px", fontSize: "0.75rem", fontWeight: 700 }}>
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <div style={{ background: "#f8fafc", padding: "0.85rem", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 600 }}>Employee ID</div>
                <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#0f172a" }}>{employee.employeeId}</div>
              </div>
              <div style={{ background: "#f8fafc", padding: "0.85rem", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 600 }}>Department</div>
                <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#0f172a" }}>{employee.department}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Drawer Footer */}
      <div style={{ padding: "1.25rem", borderTop: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={onClose}
          style={{
            background: "#1b4b31",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "0.5rem 1.1rem",
            fontSize: "0.82rem",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
